import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  FileJson,
  Loader2,
  Plus,
  RefreshCw,
  Upload,
} from 'lucide-react';

import ActionButton from '../../../../components/Buttons/ActionButton';
import FormModal from '../../../../components/Modals/FormModal';
import SupplierRegistrationPopup from '../../../../components/Supplier/SupplierRegistrationPopup';

import useAlerts from '../../../../hooks/useAlerts';
import useApiErrorHandler from '../../../../hooks/useApiErrorHandler';
import RestaurantRawMaterialIdentifierService from '../../../../services/restaurant/RestaurantRawMaterialIdentifierService';
import RestaurantUnitConversionService from '../../../../services/restaurant/RestaurantUnitConversionService';
import SupplierService from '../../../../services/supplier/SupplierService';

import { IPaymentConditionDropdown } from '../../../../types/paymentConditions/paymentConditionInterfaces';
import { IProductUnit } from '../../../../types/productUnit/productUnitInterfaces';
import { RestaurantRawMaterialCategoryDTO } from '../../../../types/restaurant/RestaurantRawMaterialCategoryTypes';
import { RestaurantRawMaterialDTO } from '../../../../types/restaurant/RestaurantRawMaterialTypes';
import {
  RestaurantPurchaseOrderForm,
  RestaurantPurchasePaymentCondition,
} from '../../../../types/restaurant/RestaurantPurchaseOrderTypes';
import {
  DteImportRow,
  ParsedDtePurchase,
} from '../../../../types/restaurant/RestaurantDteImportTypes';

import { parseDtePurchase, tipoDteLabel } from './parseDtePurchase';
import RawMaterialQuickCreateModal from '../RawMaterialQuickCreateModal';
import DteImportLineCard from './components/DteImportLineCard';
import { compatiblePurchaseUnits } from '../RestaurantPurchaseOrderFormModal';
import { RestaurantUnitConversionDTO } from '../../../../types/restaurant/RestaurantUnitConversionTypes';

interface MatchedSupplier {
  id: number;
  name: string;
  nit?: string | null;
  nrc?: string | null;
  tradeName?: string | null;
}

interface Props {
  isOpen: boolean;
  branchId: number;
  rawMaterials: RestaurantRawMaterialDTO[];
  categories: RestaurantRawMaterialCategoryDTO[];
  units: IProductUnit[];
  conversions: RestaurantUnitConversionDTO[];
  paymentConditions: IPaymentConditionDropdown[];
  savingRawMaterial: boolean;
  onCreateRawMaterial: (input: {
    name: string;
    categoryId: number;
    unitId: number;
  }) => Promise<RestaurantRawMaterialDTO | null>;
  onCreateCategory: (
    name: string
  ) => Promise<RestaurantRawMaterialCategoryDTO | null>;
  onReloadSuppliers: () => void;
  onReloadConversions?: () => void | Promise<void>;
  onClose: () => void;
  onImported: (form: RestaurantPurchaseOrderForm) => void;
}

type Row = DteImportRow;

const conditionLabel: Record<string, string> = {
  CONTADO: 'Contado',
  CREDITO: 'Crédito',
  OTRO: 'Otro',
};

const money = (value: number) =>
  new Intl.NumberFormat('es-SV', { style: 'currency', currency: 'USD' }).format(
    Number.isFinite(value) ? value : 0
  );

const buildPaymentCondition = (
  condition: string,
  list: IPaymentConditionDropdown[]
): RestaurantPurchasePaymentCondition | null => {
  const target =
    condition === 'CREDITO'
      ? 'CREDITO'
      : condition === 'OTRO'
      ? 'OTRO'
      : 'CONTADO';
  const found =
    list.find((item) => item.code === target) ||
    list.find((item) => item.code === 'CONTADO') ||
    list[0];
  if (!found) return null;
  return {
    id: found.id,
    name: found.name,
    code: (found.code ?? target) as RestaurantPurchasePaymentCondition['code'],
    creditDueDate: null,
    creditDays: null,
    creditMonths: null,
    creditYears: null,
    termId: null,
    termName: null,
    termCode: null,
  };
};

const DtePurchaseImportModal = ({
  isOpen,
  branchId,
  rawMaterials,
  categories,
  units,
  conversions,
  paymentConditions,
  savingRawMaterial,
  onCreateRawMaterial,
  onCreateCategory,
  onReloadSuppliers,
  onReloadConversions,
  onClose,
  onImported,
}: Props) => {
  const { show } = useAlerts();
  const { handle } = useApiErrorHandler();

  // Reglas de conversión creadas en esta pantalla (equivalencias de empaque que
  // el usuario captura cuando la unidad del DTE no llega a la unidad base). Se
  // fusionan con las del back para que `compatiblePurchaseUnits` las vea.
  const [extraRules, setExtraRules] = useState<RestaurantUnitConversionDTO[]>([]);
  const allConversions = useMemo(
    () => [...conversions, ...extraRules],
    [conversions, extraRules]
  );

  // MH code (uniMedida del DTE) -> unidad de nuestro catálogo.
  const unitByMhCode = useMemo(() => {
    const map = new Map<number, number>();
    units.forEach((unit) => map.set(unit.mhCode, unit.id));
    return map;
  }, [units]);

  const unitName = (id: number | null | undefined) =>
    units.find((unit) => unit.id === id)?.name ?? '';

  /**
   * Resuelve la unidad de compra de una línea:
   * - `unitId`: la del DTE (uniMedida → nuestra tabla por mhCode) si es
   *   convertible a la unidad base de la materia prima; si no, la unidad base.
   * - `dteUnitId`: la unidad de nuestro catálogo a la que mapea la uniMedida.
   * - `needsRule`: true si la unidad del DTE existe pero NO tiene ruta hasta la
   *   base (falta una equivalencia por materia prima).
   */
  const resolveLineUnitInfo = (
    rawMaterialId: number | '',
    mhUnit: number | null
  ): { unitId: number | ''; dteUnitId: number | null; needsRule: boolean } => {
    const dteUnitId = mhUnit != null ? unitByMhCode.get(mhUnit) ?? null : null;
    if (rawMaterialId === '') {
      return { unitId: dteUnitId ?? '', dteUnitId, needsRule: false };
    }
    const rm = rawMaterials.find((m) => m.id === rawMaterialId);
    if (!rm) return { unitId: dteUnitId ?? '', dteUnitId, needsRule: false };
    if (dteUnitId == null || dteUnitId === rm.unitId) {
      return { unitId: rm.unitId, dteUnitId, needsRule: false };
    }
    const compatible = compatiblePurchaseUnits(
      rawMaterialId,
      rawMaterials,
      units,
      allConversions
    );
    if (compatible.some((u) => u.id === dteUnitId)) {
      return { unitId: dteUnitId, dteUnitId, needsRule: false };
    }
    return { unitId: rm.unitId, dteUnitId, needsRule: true };
  };

  const resolveLineUnit = (
    rawMaterialId: number | '',
    mhUnit: number | null
  ): number | '' => resolveLineUnitInfo(rawMaterialId, mhUnit).unitId;

  const [parsed, setParsed] = useState<ParsedDtePurchase | null>(null);
  const [parseError, setParseError] = useState('');
  const [supplier, setSupplier] = useState<MatchedSupplier | null>(null);
  const [supplierChecked, setSupplierChecked] = useState(false);
  const [checkingSupplier, setCheckingSupplier] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [supplierPopupOpen, setSupplierPopupOpen] = useState(false);
  const [quickCreateRow, setQuickCreateRow] = useState<string | null>(null);
  const [rulingRef, setRulingRef] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setParsed(null);
    setParseError('');
    setSupplier(null);
    setSupplierChecked(false);
    setCheckingSupplier(false);
    setResolving(false);
    setRows([]);
    setExtraRules([]);
    setSupplierPopupOpen(false);
    setQuickCreateRow(null);
    setRulingRef(null);
    setSubmitting(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen]);

  // --- 1. Leer el archivo -------------------------------------------------
  const handleFile = async (file: File) => {
    setParseError('');
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const result = parseDtePurchase(json);
      if (!result.ok || !result.data) {
        setParseError(result.error ?? 'No se pudo leer el DTE.');
        return;
      }
      setParsed(result.data);
      setExtraRules([]);
      setRows(
        result.data.lines.map((line) => ({
          line,
          rawMaterialId: '',
          matched: false,
          matchedBy: null,
          preexisting: false,
          createdIdentifierIds: [],
          linking: false,
          aliasName: line.description,
          quantity: line.quantity || '',
          unitId: resolveLineUnit('', line.mhUnit),
          unitCost: line.unitPrice || '',
          needsConversionRule: false,
          dteUnitId: resolveLineUnitInfo('', line.mhUnit).dteUnitId,
        }))
      );
    } catch {
      setParseError('El archivo no es un JSON válido.');
    }
  };

  // --- 2. Buscar el proveedor por NIT / NRC -----------------------------
  const checkSupplier = async (data: ParsedDtePurchase) => {
    setCheckingSupplier(true);
    try {
      const response = await SupplierService.matchByNitOrNrc(
        data.emisor.nit,
        data.emisor.nrc
      );
      const match = response?.data as MatchedSupplier | null;
      setSupplier(match ?? null);
    } catch (error) {
      handle(error);
      setSupplier(null);
    } finally {
      setSupplierChecked(true);
      setCheckingSupplier(false);
    }
  };

  useEffect(() => {
    if (parsed) checkSupplier(parsed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed]);

  // Registro de un clic con los datos del emisor del DTE.
  const [registeringSupplier, setRegisteringSupplier] = useState(false);
  const quickRegisterSupplier = async () => {
    if (!parsed || registeringSupplier) return;
    setRegisteringSupplier(true);
    try {
      const response = await SupplierService.quickRegisterFromDte({
        nit: parsed.emisor.nit,
        nrc: parsed.emisor.nrc,
        name: parsed.emisor.name,
        tradeName: parsed.emisor.tradeName,
        phone: parsed.emisor.phone,
        email: parsed.emisor.email,
        departmentCode: parsed.emisor.departmentCode,
        municipalityCode: parsed.emisor.municipalityCode,
        address: parsed.emisor.address,
        economicActivityCode: parsed.emisor.activityCode,
      });
      const created = response?.data as MatchedSupplier | null;
      if (created) {
        setSupplier(created);
        show(
          'success',
          'Proveedor registrado',
          `${created.tradeName || created.name} quedó registrado.`
        );
      } else {
        show('error', 'Proveedor', 'No se pudo registrar el proveedor.');
      }
      onReloadSuppliers();
    } catch (error) {
      handle(error);
    } finally {
      setRegisteringSupplier(false);
    }
  };

  // --- 3. Resolver los identificadores de cada línea --------------------
  const resolveLines = async (data: ParsedDtePurchase, supplierId: number | null) => {
    setResolving(true);
    try {
      const response = await RestaurantRawMaterialIdentifierService.resolveBatch(
        branchId,
        {
          supplierId,
          items: data.lines.map((line) => ({
            ref: line.ref,
            code: line.code,
            name: line.description,
          })),
        }
      );
      const byRef = new Map(
        (response.data ?? []).map((row) => [row.ref, row])
      );
      setRows((current) =>
        current.map((row) => {
          const hit = byRef.get(row.line.ref);
          if (!hit || !hit.matched || !hit.rawMaterialId) return row;
          const info = resolveLineUnitInfo(hit.rawMaterialId, row.line.mhUnit);
          return {
            ...row,
            rawMaterialId: hit.rawMaterialId,
            matched: true,
            matchedBy: hit.matchedBy,
            preexisting: true,
            unitId: info.unitId,
            dteUnitId: info.dteUnitId,
            needsConversionRule: info.needsRule,
          };
        })
      );
    } catch (error) {
      handle(error);
    } finally {
      setResolving(false);
    }
  };

  useEffect(() => {
    if (parsed && supplierChecked) {
      resolveLines(parsed, supplier?.id ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed, supplierChecked, supplier?.id]);

  // --- Edición de filas -------------------------------------------------
  const patchRow = (ref: string, patch: Partial<Row>) =>
    setRows((current) =>
      current.map((row) => (row.line.ref === ref ? { ...row, ...patch } : row))
    );

  /**
   * Liga una línea a una materia prima y, si aún no estaba identificada, crea en
   * el acto UN identificador del proveedor: el NOMBRE con el que el proveedor
   * describe el producto en el DTE (`descripcion`). El código del documento no se
   * guarda como identificador. No se espera a "Continuar".
   */
  const linkRawMaterial = async (ref: string, rawMaterialId: number) => {
    const rm = rawMaterials.find((m) => m.id === rawMaterialId);
    const row = rows.find((r) => r.line.ref === ref);
    if (!row) return;

    const info = resolveLineUnitInfo(rawMaterialId, row.line.mhUnit);
    const unitPatch = {
      unitId: info.unitId,
      dteUnitId: info.dteUnitId,
      needsConversionRule: info.needsRule,
    };

    // Ya estaba identificada por un identificador existente: solo re-vincula.
    if (row.preexisting) {
      patchRow(ref, { rawMaterialId, ...unitPatch });
      return;
    }

    const aliasName = row.aliasName.trim();
    if (!supplier || !aliasName) {
      patchRow(ref, { rawMaterialId, ...unitPatch });
      return;
    }

    patchRow(ref, { rawMaterialId, ...unitPatch, linking: true });

    const createdIds: number[] = [];
    try {
      const response = await RestaurantRawMaterialIdentifierService.create(
        rawMaterialId,
        {
          supplierId: supplier.id,
          type: 'NAME',
          value: aliasName,
        }
      );
      if (response.data?.id) createdIds.push(response.data.id);
    } catch {
      // Un identificador duplicado no debe frenar el flujo.
    }

    patchRow(ref, {
      matched: true,
      matchedBy: 'NAME',
      preexisting: false,
      createdIdentifierIds: createdIds,
      linking: false,
    });
    show(
      'success',
      'Identificador creado',
      `Se asoció "${aliasName}" a ${
        rm?.name ?? 'la materia prima'
      } para ${supplier.name}.`
    );
  };

  /** Deshace la asociación creada en esta pantalla (borra los identificadores). */
  const undoLink = async (ref: string) => {
    const row = rows.find((r) => r.line.ref === ref);
    if (!row) return;
    patchRow(ref, { linking: true });
    for (const id of row.createdIdentifierIds) {
      try {
        await RestaurantRawMaterialIdentifierService.remove(id);
      } catch {
        // ignorar
      }
    }
    patchRow(ref, {
      rawMaterialId: '',
      matched: false,
      matchedBy: null,
      createdIdentifierIds: [],
      linking: false,
      needsConversionRule: false,
    });
  };

  /**
   * Captura la equivalencia "1 {unidad del DTE} = factor {unidad base}" para la
   * materia prima de esa línea y la persiste como regla de conversión
   * específica. Tras guardarla, la línea pasa a comprarse en la unidad del DTE.
   */
  const createConversionRule = async (ref: string, factor: number) => {
    const row = rows.find((r) => r.line.ref === ref);
    const rm = row
      ? rawMaterials.find((m) => m.id === row.rawMaterialId)
      : undefined;
    if (!row || !rm || row.dteUnitId == null || !(factor > 0)) return;

    setRulingRef(ref);
    try {
      let created: RestaurantUnitConversionDTO | null = null;
      try {
        const response = await RestaurantUnitConversionService.create({
          fromUnitId: row.dteUnitId,
          toUnitId: rm.unitId,
          rawMaterialId: rm.id,
          factor,
        });
        created = response.data ?? null;
      } catch (error) {
        const message = String(
          (error as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ?? ''
        ).toLowerCase();
        // "Ya existe una regla…" → la tratamos como éxito; cualquier otro error sí frena.
        if (!message.includes('ya existe')) {
          handle(error);
          setRulingRef(null);
          return;
        }
      }

      const rule: RestaurantUnitConversionDTO = created ?? {
        id: Date.now(),
        fromUnitId: row.dteUnitId,
        fromUnitName: '',
        fromUnitCode: '',
        toUnitId: rm.unitId,
        toUnitName: '',
        toUnitCode: '',
        rawMaterialId: rm.id,
        rawMaterialName: rm.name,
        factor,
        global: false,
        active: true,
        createdAt: '',
        updatedAt: '',
      };
      setExtraRules((prev) =>
        prev.some(
          (r) =>
            r.fromUnitId === rule.fromUnitId &&
            r.toUnitId === rule.toUnitId &&
            r.rawMaterialId === rule.rawMaterialId
        )
          ? prev
          : [...prev, rule]
      );
      patchRow(ref, { unitId: row.dteUnitId, needsConversionRule: false });
      onReloadConversions?.();
      show(
        'success',
        'Equivalencia guardada',
        `1 ${unitName(row.dteUnitId)} = ${factor} ${unitName(rm.unitId)} para ${
          rm.name
        }.`
      );
    } catch (error) {
      handle(error);
    } finally {
      setRulingRef(null);
    }
  };

  const handleQuickCreate = async (input: {
    name: string;
    categoryId: number;
    unitId: number;
  }) => {
    const created = await onCreateRawMaterial(input);
    if (created && quickCreateRow) {
      await linkRawMaterial(quickCreateRow, created.id);
    }
    setQuickCreateRow(null);
  };

  const rmOptions = useMemo(
    () =>
      rawMaterials.map((m) => ({
        id: m.id,
        name: m.name,
        code: m.code,
      })),
    [rawMaterials]
  );

  const pendingRows = rows.filter((row) => !row.rawMaterialId).length;
  const unitIssueRows = rows.filter((row) => row.needsConversionRule).length;
  const matchedCount = rows.filter((row) => row.matched).length;
  const reviewSubtotal = rows.reduce(
    (total, row) =>
      total + Number(row.quantity || 0) * Number(row.unitCost || 0),
    0
  );

  const canContinue =
    !!parsed &&
    !!supplier &&
    rows.length > 0 &&
    pendingRows === 0 &&
    unitIssueRows === 0 &&
    rows.every(
      (row) =>
        Number(row.quantity) > 0 &&
        Number(row.unitCost) >= 0 &&
        Number(row.unitId) > 0
    );

  // --- 4. Pasar a la compra (los identificadores ya se crearon arriba) ----
  const handleContinue = async () => {
    if (!parsed || !supplier || submitting) return;
    setSubmitting(true);
    try {
      const form: RestaurantPurchaseOrderForm = {
        supplierId: supplier.id,
        purchaseDate:
          parsed.meta.date || new Date().toISOString().slice(0, 10),
        paymentCondition: buildPaymentCondition(
          parsed.payment.condition,
          paymentConditions
        ),
        notes: `Importado del DTE ${parsed.meta.numeroControl}`.trim(),
        dte: {
          type: parsed.meta.tipoDte,
          controlNumber: parsed.meta.numeroControl,
          generationCode: parsed.meta.codigoGeneracion,
          taxedTotal: parsed.totals.taxedTotal,
          exemptTotal: parsed.totals.exemptTotal,
          nonSubjectTotal: parsed.totals.nonSubjectTotal,
          discountTotal: parsed.totals.discountTotal,
          ivaTotal: parsed.totals.ivaTotal,
          ivaPerception: parsed.totals.ivaPerception,
          ivaRetention: parsed.totals.ivaRetention,
          incomeRetention: parsed.totals.incomeRetention,
          operationTotal: parsed.totals.operationTotal,
          payableTotal: parsed.totals.payable,
        },
        details: rows.map((row, index) => ({
          key: Date.now() + index,
          rawMaterialId: Number(row.rawMaterialId),
          quantity: Number(row.quantity),
          unitId: Number(row.unitId),
          unitCost: Number(row.unitCost),
          discountAmount: 0,
          taxAmount: 0,
          dteTaxedSale: row.line.taxedSale,
          dteExemptSale: row.line.exemptSale,
          dteNonSubjectSale: row.line.nonSubjectSale,
          dteDiscountAmount: row.line.discount,
          expirationDate: '',
          lotNumber: '',
        })),
      };

      onImported(form);
      onClose();
    } catch (error) {
      handle(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={submitting ? undefined : onClose}
        title="Importar compra desde DTE"
        maxWidth="max-w-5xl"
      >
        {!parsed ? (
          <div className="py-4">
            <label
              htmlFor="dte-file"
              className="flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-stroke bg-gray-2/40 px-6 py-12 text-center transition hover:border-primary hover:bg-primary/5 dark:border-strokedark dark:bg-meta-4/20"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Upload className="h-7 w-7" />
              </span>
              <span className="text-sm font-semibold text-black dark:text-white">
                Sube el archivo JSON del DTE
              </span>
              <span className="max-w-md text-xs text-bodydark2">
                Factura (01) o Comprobante de Crédito Fiscal (03). Se lee el
                proveedor, los productos y la condición de pago para armar la
                compra automáticamente.
              </span>
              <input
                ref={fileRef}
                id="dte-file"
                type="file"
                accept=".json,application/json"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) handleFile(file);
                }}
              />
            </label>
            {parseError && (
              <p className="mt-4 flex items-center gap-2 rounded-lg border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {parseError}
              </p>
            )}
            <p className="mt-4 text-center text-xs text-bodydark2">
              ¿No sabes cómo debe verse el archivo?{' '}
              <a
                href="/plantillas/restaurante/compra-dte-ejemplo.json"
                download
                className="font-medium text-primary hover:underline"
              >
                Descargar DTE de ejemplo
              </a>
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Cabecera del DTE */}
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-stroke bg-gray-2/40 px-4 py-3.5 dark:border-strokedark dark:bg-meta-4/20">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <FileJson className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-black dark:text-white">
                    {tipoDteLabel(parsed.meta.tipoDte)}
                    <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary">
                      {conditionLabel[parsed.payment.condition]}
                    </span>
                  </p>
                  <p className="font-mono text-[11px] text-bodydark2">
                    {parsed.meta.numeroControl} · {parsed.meta.date}
                  </p>
                </div>
              </div>
              <div className="text-right text-xs text-bodydark2">
                <p>
                  Total DTE{' '}
                  <span className="text-sm font-bold text-black dark:text-white">
                    {money(parsed.totals.operationTotal)}
                  </span>
                </p>
                <p>IVA {money(parsed.totals.ivaTotal)}</p>
              </div>
            </div>

            {/* Proveedor */}
            <div>
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-bodydark2">
                Proveedor
              </p>
              {checkingSupplier ? (
                <div className="flex items-center gap-2 rounded-xl border border-stroke px-4 py-3 text-sm text-bodydark2 dark:border-strokedark">
                  <Loader2 className="h-4 w-4 animate-spin" /> Buscando proveedor…
                </div>
              ) : supplier ? (
                <div className="flex items-center gap-3 rounded-xl border border-success/30 bg-success/5 px-4 py-3">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-success" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-success">
                      {supplier.tradeName || supplier.name}
                    </p>
                    <p className="text-xs text-bodydark2">
                      Registrado · NIT {supplier.nit || '—'} · NRC{' '}
                      {supplier.nrc || '—'}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-warning/40 bg-warning/5 px-4 py-3">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-meta-8 dark:text-warning" />
                    <div className="min-w-0 text-xs text-body dark:text-bodydark">
                      <p className="text-sm font-semibold text-meta-8 dark:text-warning">
                        Proveedor no registrado
                      </p>
                      <p className="mt-1">
                        <span className="font-medium text-black dark:text-white">
                          {parsed.emisor.tradeName}
                        </span>{' '}
                        · NIT {parsed.emisor.nit || '—'} · NRC{' '}
                        {parsed.emisor.nrc || '—'}
                      </p>
                      {(parsed.emisor.phone || parsed.emisor.email) && (
                        <p className="mt-0.5">
                          {parsed.emisor.phone} {parsed.emisor.email}
                        </p>
                      )}
                      {parsed.emisor.address && (
                        <p className="mt-0.5">{parsed.emisor.address}</p>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <ActionButton
                      label="Registrar proveedor"
                      icon={<Plus className="h-4 w-4" />}
                      variant="primary"
                      size="sm"
                      fullWidth={false}
                      loading={registeringSupplier}
                      loadingLabel="Registrando…"
                      onClick={quickRegisterSupplier}
                    />
                    <ActionButton
                      label="Volver a verificar"
                      icon={<RefreshCw className="h-4 w-4" />}
                      variant="secondary"
                      size="sm"
                      fullWidth={false}
                      disabled={registeringSupplier}
                      onClick={() => parsed && checkSupplier(parsed)}
                    />
                    <button
                      type="button"
                      className="text-[11px] font-medium text-primary hover:underline"
                      onClick={() => setSupplierPopupOpen(true)}
                    >
                      Registro detallado
                    </button>
                  </div>
                </div>
              )}
            </div>

            {parsed.payment.condition === 'CREDITO' && (
              <p className="rounded-lg border border-primary/15 bg-primary/5 px-3 py-2 text-xs text-body dark:text-bodydark">
                El DTE es a <strong>crédito</strong>: completa el plazo en el
                formulario de compra que se abre a continuación.
              </p>
            )}

            {/* Líneas */}
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-bodydark2">
                  Productos del DTE
                </p>
                <p className="text-xs text-bodydark2">
                  {resolving ? (
                    <span className="inline-flex items-center gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" /> identificando…
                    </span>
                  ) : (
                    `${matchedCount}/${rows.length} identificadas`
                  )}
                </p>
              </div>

              {!supplier && !checkingSupplier && (
                <p className="mb-2.5 flex items-start gap-1.5 rounded-lg border border-warning/40 bg-warning/5 px-3 py-2 text-xs text-meta-8 dark:text-warning">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  Registra el proveedor arriba para poder elegir la materia
                  prima de cada línea y crear sus identificadores.
                </p>
              )}

              <div className="space-y-2.5">
                {rows.map((row) => {
                  const rm = row.rawMaterialId
                    ? rawMaterials.find((m) => m.id === row.rawMaterialId)
                    : undefined;
                  return (
                    <DteImportLineCard
                      key={row.line.ref}
                      row={row}
                      options={rmOptions}
                      supplierReady={!!supplier}
                      rawMaterial={rm}
                      dteUnitName={unitName(row.dteUnitId)}
                      baseUnitName={rm ? unitName(rm.unitId) : ''}
                      savingRule={rulingRef === row.line.ref}
                      onSelectRawMaterial={(id) =>
                        linkRawMaterial(row.line.ref, id)
                      }
                      onUndoLink={() => undoLink(row.line.ref)}
                      onCreateRawMaterial={() => setQuickCreateRow(row.line.ref)}
                      onCreateConversionRule={(factor) =>
                        createConversionRule(row.line.ref, factor)
                      }
                      onChange={(patch) => patchRow(row.line.ref, patch)}
                    />
                  );
                })}
              </div>

              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-stroke bg-gray-2/40 px-4 py-3 text-sm dark:border-strokedark dark:bg-meta-4/20">
                <span className="text-bodydark2">
                  {pendingRows > 0 ? (
                    <span className="font-medium text-meta-8 dark:text-warning">
                      {pendingRows} línea(s) sin materia prima
                    </span>
                  ) : unitIssueRows > 0 ? (
                    <span className="font-medium text-meta-8 dark:text-warning">
                      {unitIssueRows} línea(s) necesitan una equivalencia de
                      unidad
                    </span>
                  ) : (
                    'Todas las líneas están listas'
                  )}
                </span>
                <span className="text-bodydark2">
                  Subtotal compra{' '}
                  <span className="font-semibold text-black dark:text-white">
                    {money(reviewSubtotal)}
                  </span>{' '}
                  · DTE {money(parsed.totals.operationTotal)}
                </span>
              </div>
            </div>

            {/* Resumen fiscal del DTE (se guardará con la compra) */}
            <div>
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-bodydark2">
                Resumen fiscal del DTE
              </p>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-1 rounded-xl border border-stroke bg-gray-2/40 px-4 py-3 text-sm dark:border-strokedark dark:bg-meta-4/20 sm:grid-cols-3">
                {[
                  ['Gravado', parsed.totals.taxedTotal],
                  ['Exento', parsed.totals.exemptTotal],
                  ['No sujeto', parsed.totals.nonSubjectTotal],
                  ['Descuento', parsed.totals.discountTotal],
                  ['Subtotal', parsed.totals.subTotal],
                  ['IVA 13%', parsed.totals.ivaTotal],
                  ['IVA percibido', parsed.totals.ivaPerception],
                  ['IVA retenido', parsed.totals.ivaRetention],
                  ['Retención renta', parsed.totals.incomeRetention],
                ]
                  .filter(([, value]) => Number(value) !== 0)
                  .map(([label, value]) => (
                    <div key={label} className="flex justify-between gap-2">
                      <dt className="text-bodydark2">{label}</dt>
                      <dd className="font-medium text-black dark:text-white">
                        {money(Number(value))}
                      </dd>
                    </div>
                  ))}
                <div className="col-span-2 mt-1 flex justify-between gap-2 border-t border-stroke pt-1.5 sm:col-span-3 dark:border-strokedark">
                  <dt className="font-semibold text-black dark:text-white">
                    Total a pagar
                  </dt>
                  <dd className="font-bold text-black dark:text-white">
                    {money(parsed.totals.payable)}
                  </dd>
                </div>
              </dl>
              <p className="mt-1.5 text-[11px] text-bodydark2">
                Documento origen:{' '}
                <span className="font-mono">{parsed.meta.numeroControl}</span> ·{' '}
                {parsed.meta.codigoGeneracion}
              </p>
            </div>

            {/* Footer */}
            <div className="flex flex-col-reverse gap-3 border-t border-stroke pt-4 dark:border-strokedark sm:flex-row sm:justify-between">
              <ActionButton
                label="Elegir otro archivo"
                variant="secondary"
                size="md"
                fullWidth={false}
                disabled={submitting}
                onClick={reset}
              />
              <ActionButton
                label="Continuar a la compra"
                variant="primary"
                size="md"
                fullWidth={false}
                loading={submitting}
                loadingLabel="Preparando…"
                disabled={!canContinue || submitting}
                onClick={handleContinue}
              />
            </div>
          </div>
        )}
      </FormModal>

      <SupplierRegistrationPopup
        isOpen={supplierPopupOpen}
        onClose={() => setSupplierPopupOpen(false)}
        onSubmit={() => {
          setSupplierPopupOpen(false);
          onReloadSuppliers();
          if (parsed) checkSupplier(parsed);
        }}
      />

      <RawMaterialQuickCreateModal
        isOpen={quickCreateRow !== null}
        categories={categories}
        units={units}
        initialName={
          quickCreateRow
            ? rows.find((row) => row.line.ref === quickCreateRow)?.line
                .description ?? ''
            : ''
        }
        saving={savingRawMaterial}
        onClose={() => setQuickCreateRow(null)}
        onSubmit={handleQuickCreate}
        onCreateCategory={onCreateCategory}
      />
    </>
  );
};

export default DtePurchaseImportModal;
